import React, { useCallback, useRef, useState } from "react"
import { Box, Card, CardContent, CircularProgress, Divider, Stack, Typography } from "@mui/material"
import PersonIcon from "@mui/icons-material/Person"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import CMCAvatar from "@/shared/components/Avatar"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import { useUpdateProfilePicture } from "@/features/profile/hooks/useUpdateProfilePicture"

interface ProfileInfoCardProps {
  displayName: string
  email: string
  photoURL: string | null
  memberSince: Date | null
}

const ProfileInfoCard: React.FC<ProfileInfoCardProps> = ({ displayName, email, photoURL, memberSince }) => {
  const formattedDate = memberSince
    ? memberSince.toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })
    : null

  const { showSnackbar } = useSnackbar()
  const { uploadProfilePicture, isUploading } = useUpdateProfilePicture()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [hovering, setHovering] = useState(false)
  const [localPhotoURL, setLocalPhotoURL] = useState<string | null>(null)

  const displayedPhotoURL = localPhotoURL ?? photoURL

  const handleAvatarClick = useCallback(() => {
    if (!isUploading) fileInputRef.current?.click()
  }, [isUploading])

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      // Reset so the same file can be re-selected if needed
      e.target.value = ""

      const newURL = await uploadProfilePicture(file)
      if (newURL) {
        setLocalPhotoURL(newURL)
        showSnackbar("Profile picture updated!", "success")
      } else {
        showSnackbar("Failed to update profile picture. Please try again.", "error")
      }
    },
    [uploadProfilePicture, showSnackbar]
  )

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%", width: "100%" }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>
            Account Info
          </Typography>
          <Divider />
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{ position: "relative", display: "inline-flex", cursor: isUploading ? "default" : "pointer" }}
              onClick={handleAvatarClick}
              onMouseEnter={() => setHovering(true)}
              onMouseLeave={() => setHovering(false)}>
              <CMCAvatar alt={displayName} size={64} url={displayedPhotoURL} uid={null}>
                {!displayedPhotoURL && <PersonIcon />}
              </CMCAvatar>

              {(hovering || isUploading) && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    bgcolor: "rgba(0,0,0,0.45)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                  {isUploading ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    <PhotoCameraIcon sx={{ color: "white", fontSize: 22 }} />
                  )}
                </Box>
              )}

              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
            </Box>

            <Stack spacing={0.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                {displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {email}
              </Typography>
              {formattedDate && (
                <Typography variant="caption" color="text.secondary">
                  Member since {formattedDate}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default ProfileInfoCard
